import React, { type ComponentProps, useEffect, useState } from "react";

import clsx from "clsx";
import deepEqual from "deep-equal";
import _, { sortBy } from "lodash";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import {
  type CellProps,
  type Column,
  type HeaderProps,
  type Row,
  useAsyncDebounce,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from "react-table";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { usePageTitle } from "@/atoms/page-title-atom";
import { gql } from "@apollo/client";
import { ControlBar } from "@components/control-bar";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { ExpandableBreakdownSelector } from "@components/reports/breakdown-selector";
import { CompanionWrapper } from "@components/reports/control-center-utilities";
import { PaginationBar } from "@components/reports/native-table";
import { StuckToTop } from "@components/stuck-to-top";
import { LoadingSlide } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { Bubble } from "@components/utilities";
import { LogOnMount } from "@components/v2/analytics";
import styled from "@emotion/styled";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import { logEvent } from "@internal/analytics";
import {
  type BulkAddAdObjectLabel,
  type BulkAddAdObjectLabelVariables,
  type ListAdObjects,
  type ListAdObjectsVariables,
} from "@north-beam/nb-client-side";
import {
  type Breakdown,
  type BreakdownConfig,
  type CategoricalBreakdownConfig,
  Granularity,
  type IGranularity,
  parseBreakdownListQueryParam,
} from "@north-beam/nb-common";
import { enumType } from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { notifyLabelsCache } from "@shared/notify-labels-cache";
import { GranularitySelect, Select } from "@shared/selects";
import { TextInput } from "@shared/text-input";
import {
  mergeLocationSearchParams,
  toReactSelectLabel,
  toReactSelectLabelMulti,
} from "@utils/index";

import { pickColorDark } from "./label-colors";
import { AdIcon } from "./utils";

export function AdObjectList() {
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  React.useEffect(() => {
    if (breakdownConfigs.length > 1) {
      logEvent("Visit Ad Objects List");
    }
  }, [breakdownConfigs]);

  return <Inner breakdownConfigs={breakdownConfigs} />;
}

function Inner({
  breakdownConfigs,
}: {
  breakdownConfigs: CategoricalBreakdownConfig[];
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const params: QueryParams = parseQueryParams(location.search);
  const variables: ListAdObjectsVariables = {
    activeOnly: params.showCampaignsMode === "activeOnly",
    filters: params.filters,
    granularity: params.granularity,
  };

  const { loading, data } = useNorthbeamQuery<
    ListAdObjects,
    ListAdObjectsVariables
  >(LIST_CAMPAIGNS, {
    variables,
    useHeavy: true,
  });

  const updateQuery = React.useCallback(
    (newQuery: QueryParams) => {
      const search = location.search;
      const newParams = makeQueryString(newQuery);
      navigate({ search: mergeLocationSearchParams(search, newParams) });
    },
    [navigate, location.search],
  );

  return (
    <div>
      <StuckToTop>
        {() => (
          <ControlCenter
            initial={params}
            onSubmit={updateQuery}
            isLoading={loading}
            breakdownConfigs={breakdownConfigs}
          />
        )}
      </StuckToTop>
      <CompanionWrapper>
        <DynamicArea
          loading={loading}
          data={data}
          breakdownConfigs={breakdownConfigs}
          params={params}
        />
      </CompanionWrapper>
    </div>
  );
}

function DynamicArea({
  loading,
  data,
  breakdownConfigs,
  params,
}: {
  loading: boolean;
  data: any;
  breakdownConfigs: BreakdownConfig[];
  params: QueryParams;
}) {
  const [, setPageTitle] = usePageTitle();
  useEffect(() => {
    setPageTitle("Breakdowns Editor");
  }, [setPageTitle]);

  if (loading) {
    return <LoadingSlide />;
  }

  const { adObjects } = data.me;

  return (
    <div className="container-xl px-4">
      <LogOnMount name="Visit Traffic Source Labels Page" />
      <AdObjectListTable
        breakdownConfigs={breakdownConfigs}
        adObjects={adObjects}
        isLoading={loading}
        granularity={params.granularity}
      />
    </div>
  );
}

function Labels(props: CellProps<any>) {
  const divEl: React.RefObject<HTMLDivElement> = React.useRef(null);
  const [size, setSize] = React.useState<"pending" | "ok" | "overflow">(
    "pending",
  );
  const [collapsed, setCollapsed] = React.useState<boolean>(true);

  const labels: { key: string; value: string; isManualLabel: boolean }[] =
    sortBy(props.cell.value, "key");

  React.useEffect(() => {
    /* TODO: not perfect but works ok -- this effect needs to re-fire whenever
       TODO: the client window changes */
    if (divEl.current) {
      const { clientHeight, scrollHeight } = divEl.current;
      setSize(clientHeight < scrollHeight ? "overflow" : "ok");
    } else {
      setSize("pending");
    }
  }, []);

  return (
    <div className="flex">
      <div
        className="text-wrap flex-wrap w-100"
        ref={divEl}
        style={{
          lineHeight: "1.5rem",
          maxHeight: collapsed ? "2rem" : "none",
          overflowY: "hidden",
        }}
      >
        {labels.length === 0 && <Bubble color="transparent">&nbsp;</Bubble>}
        {labels.map((label: any) => (
          <GenericTooltip
            content={`${label.key} = ${label.value}`}
            key={JSON.stringify(label)}
            noInfoCircle
          >
            <Label label={label} key={JSON.stringify(label)} />
          </GenericTooltip>
        ))}
      </div>
      <div className="flex-shrink-1">
        <button
          className="btn btn-link btn-sm"
          style={{ fontSize: "12px" }}
          onClick={() => setCollapsed(!collapsed)}
        >
          {size !== "overflow" ? null : collapsed ? (
            <>
              <i className="fa-regular fa-sharp fa-chevron-down" />
              &nbsp;More
            </>
          ) : (
            <>
              <i className="fa-regular fa-sharp fa-chevron-up" />
              &nbsp;Less
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function Label(props: {
  label: { key: string; value: string; isManualLabel: boolean };
}) {
  const truncLimit = 15;
  const { label } = props;
  const { key, value } = label;
  const truncatedKey =
    key.length > truncLimit ? key.substr(0, truncLimit - 3) + "..." : key;
  const truncatedValue =
    value.length > truncLimit ? value.substr(0, truncLimit - 3) + "..." : value;
  const bgColor = pickColorDark(key);
  return (
    <Bubble color={bgColor} title={`${key} = ${value}`}>
      <span className="text-truncate">
        {props.label.isManualLabel && "**"}
        {truncatedKey}
      </span>{" "}
      <span className="text-truncate">=</span>{" "}
      <span className="text-truncate">{truncatedValue}</span>
    </Bubble>
  );
}

const Styles = styled.div`
  /* This is required to make the table full-width */
  display: block;
  max-width: 100%;
  font-size: 14px;
  margin-top: 16px;

  /* This will make the table scrollable when it gets too small */
  .table-wrap {
    display: block;
    max-width: 100%;
    overflow-x: hidden;
    overflow-y: hidden;
  }

  table {
    /* Make sure the inner table is always as wide as needed */
    width: 100%;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;

      :last-child {
        border-right: 0;
      }
    }
  }
`;

function AdObjectListTable(props: {
  adObjects: any[];
  isLoading: boolean;
  breakdownConfigs: BreakdownConfig[];
  granularity: IGranularity;
}) {
  const [manual] = useManualBreakdowns();
  const {
    isLoading,
    adObjects: adObjects_,
    breakdownConfigs,
    granularity,
  } = props;
  const adObjects = React.useMemo(
    () =>
      adObjects_
        .map((v) => {
          const hasManualAssignment = v.labelsDigest.find(
            (i: any) => i.isManualLabel,
          );
          return {
            ...v,
            numLabels: v.labelsDigest.length,
            manuallyAssigned: hasManualAssignment ? "Yes" : "No",
          };
        })
        .filter((i) => {
          if (manual === "manual") {
            return i.manuallyAssigned === "Yes";
          }
          return true;
        }),
    [adObjects_, manual],
  );

  const bds = breakdownConfigs as CategoricalBreakdownConfig[];
  const defaultColumn: Column<any> = React.useMemo(
    () => ({ disableSortBy: true } as unknown as Column<any>),
    [],
  );

  const labelsSortFunc = React.useCallback((a: Row<any>, b: Row<any>) => {
    const aHasMoreLabels = a.original.numLabels > b.original.numLabels;
    return aHasMoreLabels ? 1 : -1;
  }, []);

  const columns: Column<any>[] = React.useMemo(
    () =>
      [
        {
          id: "selection",
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            </div>
          ),
          Cell: ({ row }: CellProps<any>) => (
            <div>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          ),
        },
        {
          Header: Granularity.getLabel(granularity),
          accessor: "name",
          filter: "text",
          Cell: ({ cell, row }: CellProps<any>) => (
            <Link title={cell.value} to={row.original.id}>
              <div className="flex items-center">
                <AdIcon sizeInEM={1} platform={row.original.nbtPlatformID} />
                <div className="text-truncate" style={{ maxWidth: "18rem" }}>
                  {cell.value}
                </div>
              </div>
            </Link>
          ),
        },
        {
          Header: "Manual assigned",
          accessor: "manuallyAssigned",
        },
        {
          Header: "Status",
          accessor: "status",
        },
        {
          disableSortBy: false,
          Header: ({ column }: HeaderProps<any>) => {
            return (
              <span>
                {"Labels  "}
                {column.isSorted ? (
                  column.isSortedDesc ? (
                    <i className="fa-regular fa-sharp fa-sort-down"></i>
                  ) : (
                    <i className="fa-regular fa-sharp fa-sort-up"></i>
                  )
                ) : (
                  <i className="fa-regular fa-sharp fa-sort"></i>
                )}
              </span>
            );
          },
          sortType: labelsSortFunc,
          accessor: "labelsDigest",
          Cell: Labels,
        },
      ] as Column<any>[],
    [labelsSortFunc, granularity],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { selectedRowIds, globalFilter, pageIndex },
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
  } = useTable<any>(
    {
      columns,
      data: adObjects,
      defaultColumn,
      autoResetGlobalFilter: false,
      initialState: { pageSize: 20 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );

  const paginationProps = {
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    pageIndex,
  };

  const RenderRow = React.useCallback(
    (row: Row<any>) => {
      prepareRow(row);

      const style = (row as any).style;
      return (
        <tr
          {...row.getRowProps({ style })}
          className={row.isSelected ? "table-primary" : ""}
        >
          {row.cells.map((cell) => (
            // eslint-disable-next-line
            <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
          ))}
        </tr>
      );
    },
    [prepareRow],
  );

  const [keyInputValue, setKeyInputValue] = React.useState<string | null>(null);
  const [valueInputValue, setValueInputValue] = React.useState<string | null>(
    null,
  );

  const numSelectedIds = Object.values(selectedRowIds).filter((v) => v).length;
  const keyOptions = toReactSelectLabelMulti(bds.map((v) => v.key));
  const currentBreakdown = !keyInputValue
    ? undefined
    : bds.find((v) => v.key === keyInputValue);
  const valueOptions = (currentBreakdown && currentBreakdown.choices) ?? [];

  const [bulkAddAdObjectLabel, { loading: isMutating }] = useNorthbeamMutation<
    BulkAddAdObjectLabel,
    BulkAddAdObjectLabelVariables
  >(BULK_ADD_AD_OBJECT_LABEL, { useCpuIntensive: true });

  const trySubmit = React.useCallback(async () => {
    if (!keyInputValue || !valueInputValue || isMutating) {
      return;
    }

    const selectIds = Object.keys(selectedRowIds).map(
      (k) => adObjects[parseInt(k)].id,
    );
    const variables = {
      ids: selectIds,
      labelKey: keyInputValue,
      labelValue: valueInputValue,
    };

    await bulkAddAdObjectLabel({ variables });
    setKeyInputValue(null);
    setValueInputValue(null);

    notifyLabelsCache();
  }, [
    adObjects,
    bulkAddAdObjectLabel,
    isMutating,
    selectedRowIds,
    keyInputValue,
    valueInputValue,
    setKeyInputValue,
    setValueInputValue,
  ]);

  return (
    <Styles className="bg-white rounded-md p-5">
      <PaginationBar {...paginationProps} />
      <nav className="navbar border-top border-left border-right bg-white">
        <ul className="nav nav-pills">
          <span className="nav-link">
            <strong>{numSelectedIds}</strong> selected
          </span>
          {(numSelectedIds > 0 && (
            <div className="form-inline">
              <div className="d-inline mr-2" style={{ width: 150 }}>
                <CreatableSelect
                  isClearable
                  options={keyOptions}
                  isDisabled={isMutating}
                  onChange={(value: any) =>
                    setKeyInputValue(value?.value ?? null)
                  }
                  value={toReactSelectLabel(keyInputValue)}
                  placeholder="Enter key..."
                />
              </div>
              <div className="d-inline mr-2" style={{ width: 150 }}>
                <CreatableSelect
                  className="mr-2"
                  isClearable
                  isDisabled={!keyInputValue || isMutating}
                  value={toReactSelectLabel(valueInputValue)}
                  onChange={(value: any) =>
                    setValueInputValue(value?.value ?? null)
                  }
                  options={valueOptions}
                  placeholder="Enter value..."
                />
              </div>
              <PrimaryButton
                disabled={!keyInputValue || !valueInputValue || isMutating}
                onClick={trySubmit}
              >
                Bulk Label
              </PrimaryButton>
            </div>
          )) || (
            <li className="nav-item">
              <span className="flex mt-2">
                <strong className="mr-1">Pro tip:</strong> Use the checkboxes to
                label multiple Traffic Sources at once.
              </span>
              <span className="ml-[3rem]">
                ** references a label which has been manually assigned.
              </span>
            </li>
          )}
        </ul>
        <ul className="nav nav-right">
          <li className="nav-item">
            <GlobalFilter
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
            />
          </li>
        </ul>
      </nav>
      <div
        className={clsx(
          isLoading && "waiting-interstitial",
          "table-wrap bg-white",
        )}
      >
        <table {...getTableProps()} className="table table-bordered">
          <thead className="thead-light">
            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  // eslint-disable-next-line
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>{page.map(RenderRow)}</tbody>
        </table>
      </div>
    </Styles>
  );
}

export interface GlobalFilterProps {
  globalFilter: string;
  setGlobalFilter: React.Dispatch<React.SetStateAction<string>>;
}

export const useManualBreakdowns = () => {
  const [manual, setManual] = useQueryParam(
    "manual",
    withDefault(StringParam, "all"),
  );
  return [manual, setManual] as const;
};

export function GlobalFilter({
  globalFilter,
  setGlobalFilter,
}: GlobalFilterProps) {
  const [value, setValue] = React.useState<string>(globalFilter);
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  const [manual, setManual] = useManualBreakdowns();

  return (
    <div className="flex items-end space-x-2">
      <Select
        label="Manual filter"
        onChange={(v) => setManual(v)}
        icon={<i className="fa-regular fa-sharp fa-database" />}
        value={manual}
        options={[
          { value: "all", label: "All" },
          { value: "manual", label: "Manual Only" },
        ]}
      />
      <TextInput
        placeholder="Filter by name..."
        leftIcon={<i className="fa-regular fa-sharp fa-search" />}
        value={value || ""}
        onChange={(event) => {
          const value = event.target.value || undefined;
          setValue(value);
          onChange(value);
        }}
      />
    </div>
  );
}

interface QueryParams {
  readonly showCampaignsMode: IShowCampaignsMode;
  readonly filters: Breakdown[];
  readonly granularity: IGranularity;
}

interface ControlCenterProps {
  onSubmit: (query: QueryParams) => void;
  isLoading: boolean;
  breakdownConfigs: BreakdownConfig[];
  initial: QueryParams;
}

function ControlCenter({
  onSubmit,
  isLoading,
  breakdownConfigs,
  initial,
}: ControlCenterProps) {
  const [proposed, setProposed] = useState(initial);

  useEffect(() => {
    setProposed(initial);
  }, [initial]);

  return (
    <ControlBar>
      <div className="flex w-full justify-between">
        <div className="flex flex-grow space-x-2 max-w-6xl">
          <Select
            label="CUSTOMIZE"
            isLoading={isLoading}
            onChange={(showCampaignsMode) =>
              setProposed({ ...proposed, showCampaignsMode })
            }
            icon={<i className="fa-regular fa-sharp fa-database" />}
            value={proposed.showCampaignsMode}
            options={ShowCampaignsMode.reactSelectOptions}
          />
          <GranularitySelect
            hasPlatform={false}
            isLoading={isLoading}
            onChange={(g) => setProposed({ ...proposed, granularity: g })}
            /* This type coercion was done for purposes of not disrupting functionality when
               solidifying the types for the GranularitySelect.  Previously, this component did
               not specify 'hasPlatform', but the GranularitySelect's default for 'hasPlatform' is
               false.  This means that the granularity it accepts, and the granularity it includes
               via 'onChange', excludes "platform".  However, the typings in this component are
               allowing the granularity to include "platform" - which is incorrect.  For now,
               however, we simply coerce the type to exclude "platform" to avoid breaking changes.
               */
            granularity={
              proposed.granularity as Exclude<IGranularity, "platform">
            }
          />
          <ExpandableBreakdownSelector
            label="FILTERS"
            isLoading={isLoading}
            current={proposed.filters}
            available={breakdownConfigs}
            allConfigs={breakdownConfigs}
            onUpdate={(filters) => setProposed((v) => ({ ...v, filters }))}
          />
        </div>
        <PrimaryButton
          size="md"
          disabled={isLoading || deepEqual(initial, proposed)}
          onClick={() => onSubmit(proposed)}
          className="justify-self-end align-self-end"
        >
          UPDATE
        </PrimaryButton>
      </div>
    </ControlBar>
  );
}

const IndeterminateCheckbox = React.forwardRef<
  HTMLInputElement,
  ComponentProps<"input"> & {
    className?: string;
    style?: React.CSSProperties;
    indeterminate?: boolean;
  }
>(
  (
    {
      indeterminate,
      ...rest
    }: ComponentProps<"input"> & {
      className?: string;
      style?: React.CSSProperties;
      indeterminate?: boolean;
    },
    ref,
  ) => {
    const defaultRef = React.useRef<HTMLInputElement>(null);
    const resolvedRef:
      | React.MutableRefObject<HTMLInputElement>
      | React.ForwardedRef<HTMLInputElement> = ref || defaultRef;

    React.useEffect(() => {
      /* This is wonky ref logic and should be fixed.

         Even though the 'resolvedRef' can be a ForwardRef, we are doing a type coercion here to
         avoid breaking changes when updating the types for this component.  The coercion here is
         incorrect, and should be fixed - because the 'resolvedRef' can be a ForwardRef - which does
         not have a current attribute.

         Additionally, the indeterminate prop (which can be undefined) is not a valid value for the
         "indeterminate" attribute on an HTMLInputElement - it must be defined.  This should be
         fixed as well.
         */
      (
        resolvedRef as React.MutableRefObject<HTMLInputElement>
      ).current.indeterminate =
        indeterminate as HTMLInputElement["indeterminate"];
    }, [resolvedRef, indeterminate]);

    return <input type="checkbox" ref={resolvedRef} {...rest} />;
  },
);

function parseQueryParams(query: string): QueryParams {
  const params = new URLSearchParams(query);

  const showCampaignsMode =
    ShowCampaignsMode.fromString(params.get("showCampaignsMode")) ?? "all";
  const filters = parseBreakdownListQueryParam(params.get("filters")) ?? [];
  const granularity =
    Granularity.fromString(params.get("granularity")) ?? "campaign";

  return {
    showCampaignsMode,

    filters,
    granularity,
  };
}

export function makeQueryString(state: QueryParams) {
  const search = new URLSearchParams();
  search.set("filters", JSON.stringify(state.filters));
  search.set("showCampaignsMode", String(state.showCampaignsMode || "all"));
  search.set("granularity", state.granularity);
  return search;
}

const LIST_CAMPAIGNS = gql`
  query ListAdObjects(
    $activeOnly: Boolean!
    $filters: [JSONObject!]!
    $granularity: String!
  ) {
    me {
      id
      adObjects(
        activeOnly: $activeOnly
        filters: $filters
        granularity: $granularity
      ) {
        id
        adKey
        name
        status
        nbtPlatformID
        labelsDigest {
          key
          value
          isManualLabel
        }
      }
    }
  }
`;

const BULK_ADD_AD_OBJECT_LABEL = gql`
  mutation BulkAddAdObjectLabel(
    $ids: [ID!]!
    $labelKey: String!
    $labelValue: String!
  ) {
    bulkAddAdObjectLabel(
      ids: $ids
      labelKey: $labelKey
      labelValue: $labelValue
      manual: true
    ) {
      id
      labels
    }
  }
`;

enum ShowCampaignsModeEnum {
  all = "Show all",
  activeOnly = "Show active only",
}

const ShowCampaignsMode = enumType({
  enumObject: ShowCampaignsModeEnum,
  keyOrder: ["all", "activeOnly"],
});

type IShowCampaignsMode = keyof typeof ShowCampaignsModeEnum;
